import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Card, Tabs, Breadcrumb, Layout, Skeleton, Empty } from 'antd';
import { CalendarOutlined, HomeOutlined, FilePdfOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AppHeaderHomePageTemplate from '../Organism/AppHeaderHomePageTemplate';
import { format } from 'date-fns';
import './ApteanCommunique.css';
import { useTranslation } from 'react-i18next';

const { TabPane } = Tabs;

interface CommuniqueDetails {
    headerText: string;
    description: string;
    imageName: string;
    createdDate: Date;
    communiqueId: string;
    pdfFileName: string;
}

// Derive image path from tab + child tab
const getImagePath = (tab: string, childTab: string, imageName: string): string => {
    if (tab === 'announcement') return `images/Announcement/${imageName}`;
    if (tab === 'newsletter')   return `images/NewsLetter/${imageName}`;
    const sub = childTab.charAt(0).toUpperCase() + childTab.slice(1); // "it" → "It" ≡ folder names
    return `images/Alerts/${sub}/${imageName}`;
};

const FALLBACK_IMG = 'images/placeholder.png';

// Responsive card grid — fills the row and wraps automatically
const GRID_STYLE: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
    gap: '16px',
};

// Reusable card — images are lazy-loaded natively
const CommuniqueCard: React.FC<{
    card: CommuniqueDetails;
    tab: string;
    childTab: string;
    onClick: () => void;
}> = ({ card, tab, childTab, onClick }) => (
    <Card
        bordered
        className="communique-card"
        bodyStyle={{ padding: '12px 14px' }}
        style={{ width: '100%', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}
        cover={
            <img
                alt={card.headerText}
                src={getImagePath(tab, childTab, card.imageName)}
                loading="lazy"
                decoding="async"
                onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK_IMG; }}
                style={{ objectFit: 'cover', height: '150px', width: '100%', cursor: 'pointer' }}
                onClick={onClick}
            />
        }
    >
        <div className="cq-title" onClick={onClick}>{card.headerText}</div>
        <p className="cq-desc">{card.description}</p>
        <p style={{ fontWeight: 400, fontSize: '12px', color: '#000', margin: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span><CalendarOutlined /> {format(new Date(card.createdDate), 'dd MMM, yyyy')}</span>
            {card.pdfFileName && (
                <a href={`pdfs/${card.pdfFileName}`} target="_blank" rel="noopener noreferrer" style={{ color: '#000' }}>
                    <FilePdfOutlined style={{ color: '#d32f2f' }} />
                </a>
            )}
        </p>
    </Card>
);

// Skeleton placeholder grid while loading
const SkeletonGrid: React.FC = () => (
    <div style={GRID_STYLE}>
        {Array.from({ length: 8 }).map((_, i) => (
            <Card key={i} style={{ width: '100%', borderRadius: '10px' }}>
                <Skeleton.Image active style={{ width: '100%', height: '120px', marginBottom: 12 }} />
                <Skeleton active paragraph={{ rows: 2 }} />
            </Card>
        ))}
    </div>
);

const ApteanCommunique: React.FC = () => {
    const [activeTab, setActiveTab]           = useState('announcement');
    const [activeChildTab, setActiveChildTab] = useState('it');
    const [loading, setLoading]               = useState(false);
    const [communiqueData, setCommuniqueData] = useState<CommuniqueDetails[]>([]);
    const { instance, accounts } = useMsal();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const baseURL = process.env.REACT_APP_API_URL;

    // Per-tab cache: key = "tab_childTab", value = { data, timestamp }
    const CACHE_TTL = 5 * 60 * 1000; // 5 minutes — re-fetch after this to pick up new data
    const cacheRef = useRef<Map<string, { data: CommuniqueDetails[]; ts: number }>>(new Map());
    // Monotonic request id — only the latest tab switch may apply its results,
    // so a slower earlier response can't overwrite a newer tab's data.
    const seqRef = useRef(0);

    const fetchToken = useCallback(async () => {
        const res = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
        return res.idToken;
    }, [instance, accounts]);

    const fetchData = useCallback(async (tab: string, childTab: string) => {
        const mySeq = ++seqRef.current; // claim latest; supersedes any in-flight fetch
        const cacheKey = tab === 'alerts' ? `${tab}_${childTab}` : tab;

        // Serve from cache if still fresh (within TTL)
        const cached = cacheRef.current.get(cacheKey);
        if (cached && Date.now() - cached.ts < CACHE_TTL) {
            setCommuniqueData(cached.data);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const token = await fetchToken();
            const url = tab === 'alerts'
                ? `${baseURL}/api/COECommunique/GetAllActiveCommuniqueByCategory?category=${tab}&sub_category=${childTab}`
                : `${baseURL}/api/COECommunique/GetAllActiveCommuniqueByCategory?category=${tab}`;

            const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
            const data: CommuniqueDetails[] = response.status === 200 ? response.data : [];
            cacheRef.current.set(cacheKey, { data, ts: Date.now() });
            // Discard stale results if the user has since switched tabs.
            if (mySeq !== seqRef.current) return;
            setCommuniqueData(data);
        } catch (error) {
            console.error('Unexpected error in fetchData:', error);
        } finally {
            if (mySeq === seqRef.current) setLoading(false);
        }
    }, [fetchToken, baseURL]);

    useEffect(() => {
        fetchData(activeTab, activeChildTab);
    }, [activeTab, activeChildTab, fetchData]);

    const handleTabChange = (key: string) => {
        setActiveTab(key);
    };

    const handleChildTabKey = (key: string) => {
        setActiveChildTab(key);
        setActiveTab('alerts');
    };

    const handleCardClick = (id: string, tab: string, child: string) => {
        navigate('/ViewCommunique', { state: { communique_id: id, activeTab: tab, activeChildTab: child } });
    };

    const renderCards = (tab: string, childTab: string) => {
        if (!communiqueData || communiqueData.length === 0) {
            return (
                <div style={{ padding: '48px 0' }}>
                    <Empty description={t('no_communique_available', { defaultValue: 'No communiqués available' })} />
                </div>
            );
        }
        return (
            <div style={GRID_STYLE}>
                {communiqueData.map((card, i) => (
                    <CommuniqueCard
                        key={card.communiqueId ?? i}
                        card={card}
                        tab={tab}
                        childTab={childTab}
                        onClick={() => handleCardClick(card.communiqueId, tab, childTab)}
                    />
                ))}
            </div>
        );
    };

    return (
        <Layout style={{ background: '#F9FAFB' }}>
            <AppHeaderHomePageTemplate />
            <style>{`
                /* Card hover + line clamping */
                .communique-card { transition: transform .18s ease, box-shadow .18s ease; }
                .communique-card:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 8px 20px rgba(134,41,164,0.15) !important;
                }
                .communique-card .cq-title {
                    font-weight: 600; font-size: 14px; color: #000; cursor: pointer;
                    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
                    overflow: hidden; min-height: 40px; line-height: 20px;
                }
                .communique-card .cq-desc {
                    font-weight: 400; font-size: 12px; color: #888;
                    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
                    overflow: hidden; min-height: 32px; margin: 6px 0 8px;
                }
                /* Brand-purple tabs (default antd is blue) */
                .communique-tabs .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn { color: #8629A4 !important; font-weight: 600; }
                .communique-tabs.ant-tabs-card > .ant-tabs-nav .ant-tabs-tab-active { border-top: 2px solid #8629A4 !important; }
                .communique-tabs .ant-tabs-ink-bar { background: #8629A4 !important; }
            `}</style>
            <div style={{ maxWidth: '100%', margin: '0 auto', padding: '0px', minHeight: '100vh', width: '62rem' }}>

                <Breadcrumb style={{ backgroundColor: 'white', padding: '20px 20px 10px' }}>
                    <Breadcrumb.Item>
                        <Link to="/home"><HomeOutlined style={{ marginRight: '5px' }} />{t('home')}</Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>{t('communique')}</Breadcrumb.Item>
                </Breadcrumb>

                <div style={{ backgroundColor: 'white', padding: '0px 20px', fontWeight: 600, fontSize: '22px', color: '#000' }}>
                    {t('communique')}
                </div>
                <div style={{ backgroundColor: 'white', padding: '0px 20px', fontWeight: 400, fontSize: '12px', color: '#000' }}>
                    {t('communique_heading')}
                </div>

                <Tabs
                    className="communique-tabs"
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    type="card"
                    style={{ marginBottom: '10px', backgroundColor: 'white', padding: '15px 10px' }}
                >
                    <TabPane tab={t('communique')} key="announcement">
                        {loading ? <SkeletonGrid /> : renderCards('announcement', '')}
                    </TabPane>

                    <TabPane tab={t('newsletters')} key="newsletter">
                        {loading ? <SkeletonGrid /> : renderCards('newsletter', '')}
                    </TabPane>

                    <TabPane tab={t('alerts')} key="alerts">
                        <Tabs className="communique-tabs" type="card" style={{ marginTop: '0px' }} activeKey={activeChildTab} onChange={handleChildTabKey}>
                            <TabPane tab={t('it')} key="it">
                                {loading ? <SkeletonGrid /> : renderCards('alerts', 'it')}
                            </TabPane>
                            <TabPane tab={t('hr')} key="hr">
                                {loading ? <SkeletonGrid /> : renderCards('alerts', 'hr')}
                            </TabPane>
                            <TabPane tab={t('finance')} key="finance">
                                {loading ? <SkeletonGrid /> : renderCards('alerts', 'finance')}
                            </TabPane>
                            <TabPane tab={t('facilities')} key="facilities">
                                {loading ? <SkeletonGrid /> : renderCards('alerts', 'facilities')}
                            </TabPane>
                        </Tabs>
                    </TabPane>
                </Tabs>

            </div>
        </Layout>
    );
};

export default ApteanCommunique;
